import React, {memo, useEffect, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import cx from 'classnames'
import queryString from 'query-string'

/* Hooks */
import useQueryParams from '../../../hooks/useQueryParams'

/* Components */
import HbClientSideHelmet from '../../../components/Helmet/HbClientHelmet'
import HbBreadCrumbView from '../../../components/Breadcrumb/HbBreadCrumbView'
import hbPageWithUserInfo from '../../../components/page/hbPageWithUserInfo'
import hbPageWithAnalytics from '../../../components/page/hbPageWithAnalytics'
import VoltranFragment from '../../../components/VoltranFragment/VoltranFragment'
import MoriaComponents from '../../../components/MoriaComponentsView/MoriaComponentsView'
import SearchKeywordRowListView from '../../../views/search/keyword/row/list/view/SearchKeywordRowListView'
import ProductListStructuredData from '../../../components/ProductListStructuredData/ProductListStructuredData'

/* Actions */
import {actionCreators as featureToggleActionCreators} from '../../../redux/actions/featureToggleActions'
import {actionCreators as brandActionCreators} from '../../../redux/actions/brandActions'
import {actionCreators as seoActionCreators} from '../../../redux/actions/seoActions'
import {actionCreators as voltranActionCreators} from '../../../redux/actions/voltranActions'
import {actionCreators as analyticsActionCreators} from '../../../redux/actions/analyticsActions'
import {actionCreators as categoryActionCreators} from '../../../redux/actions/categoryActions'
import {actionCreators as footerActionCreators} from '../../../redux/actions/footerActions'

/* Selectors */
import {seoBreadCrumbConfigArraySelector} from '../../../redux/selectors/seoSelector'

/* Utils */
import LoadEventManager from '../../../core/event/LoadEventManager'
import {generateFeatureToggleMap} from '../../../modules/feature-toggle/featureToggleUtils'
import {resolvePromiseList, resolveWithSettleContext} from '../../../common/async/promiseUtils'
import {
  generateBrandPageDesktopWebtrekkData,
  generateUserInfoForWebtrekk
} from '../../../modules/analytics/webtrekk/webtrekkUtils'
import {
  checkForBrandPageForbiddenUrlsRedirection,
  checkForDesktopBrandPageRedirection,
  isBrandPageApiPaginationLimitExceeded
} from '../../../modules/brand/brandUtils'
import {
  generateQueryString,
  removeQueryStringFromUrl
} from '../../../common/url/urlUtils'
import {getCookiesByPrefix, jsonParse} from '../../../utils/objectUtils'
import MoriaComponentsHelper from '../../../modules/moriaComponents/moriaComponentsHelper';

/* Constants */
import {FEATURE_TOGGLE_NAMES} from '../../../modules/feature-toggle/featureToggleConstants'
import {PAGINATION_PARAM_NAMES} from '../../../common/pagination/paginationConstants'
import {DAVINCI_PAGE_TYPES, DAVINCI_PAGES} from '../../../modules/analytics/davinci/davinciConstants'
import {HTTP_STATUS_CODES} from '../../../common/network/httpConstants'
import {
  DESKTOP_COMPONENTS,
  DESKTOP_COMPONENTS_OPTIONS,
} from '../../../modules/voltran/voltranConstants'
import {FACET_QUERY_PARAM_NAMES} from '../../../views/filter/facet/facetConstants'
import {PAGE_TYPES} from '../../../modules/seo/seoConstants'
import {MORIA_PASS_COOKIE_PREFIXES} from '../../../modules/moriaComponents/moriaComponentsConstants'
import {CATEGORY_SEARCH_KEYWORD_TITLE} from '../../../modules/category/categoryConstants'

/* Config */
import appConfig from '../../../appConfig'

/* Styles */
import s from './brand-page.scss'

const BRAND_PAGE_FEATURE_TOGGLES = [
  FEATURE_TOGGLE_NAMES.DISPLAY_GLOBAL_CAMPAIGN_TEXT,
  FEATURE_TOGGLE_NAMES.USE_ARWEN_FOR_SUGGESTIONS_ON_MOBILE,
  FEATURE_TOGGLE_NAMES.PAGE_LIMIT_IN_LISTING_PAGES,
  FEATURE_TOGGLE_NAMES.DISPLAY_ITEM_COUNT_ON_CART_SF,
  FEATURE_TOGGLE_NAMES.DISPLAY_RECOMMENDATIONS,
  FEATURE_TOGGLE_NAMES.DISPLAY_FEEDBACK,
  FEATURE_TOGGLE_NAMES.DISPLAY_THIRD_PRICE_WITH_BASKET,
  FEATURE_TOGGLE_NAMES.USE_HIGH_QUALITY_IMAGES,
  FEATURE_TOGGLE_NAMES.ENABLE_ENSIGHTEN,
  FEATURE_TOGGLE_NAMES.ENABLE_ABTEST,
  FEATURE_TOGGLE_NAMES.SERVER_SIDE_RENDER_FOR_PWA_OTHER_PAGE,
  FEATURE_TOGGLE_NAMES.SERVER_TO_CLIENT_SEND_INITIAL_DATA_FOR_PWA_OTHER_PAGE,
  FEATURE_TOGGLE_NAMES.DISPLAY_WHATSAPP_ICON,
  FEATURE_TOGGLE_NAMES.DISPLAY_LIKE_BUTTON_ON_LISTING_PAGES,
  FEATURE_TOGGLE_NAMES.USE_CHECKOUT_VOLTRAN_ADD_TO_CART_OTHER_PAGE,
  FEATURE_TOGGLE_NAMES.DISPLAY_JET_DELIVERY_WIDGET,
  FEATURE_TOGGLE_NAMES.ENABLE_GTM,
  FEATURE_TOGGLE_NAMES.DISPLAY_CAMPAIGN_FILTER,
  FEATURE_TOGGLE_NAMES.SERVER_SIDE_RENDER_FOR_PWA_BRAND_PAGE,
  FEATURE_TOGGLE_NAMES.SERVER_TO_CLIENT_SEND_INITIAL_DATA_FOR_PWA_BRAND_PAGE,
  FEATURE_TOGGLE_NAMES.HIDE_JS_FROM_GOOGLEBOT,
  FEATURE_TOGGLE_NAMES.DISPLAY_NEXT_DAY_DELIVERY,
  FEATURE_TOGGLE_NAMES.DISPLAY_EXPRESS_DELIVERY,
  FEATURE_TOGGLE_NAMES.DISPLAY_HEPSIGLOBAL_MODAL,
  FEATURE_TOGGLE_NAMES.IS_CRITICAL_CSS,
  FEATURE_TOGGLE_NAMES.DISPLAY_POPULAR_SEARCH_KEYWORDS_ON_CATEGORY_PAGE
]

const brandTabNameEnums = {
  BRAND_ROW: 'BrandRow',
  MORDOR_PRODUCTS: 'MordorProducts'
}

const brandTabLabelEnums = {
  BRAND_ROW: 'Vitrin',
  MORDOR_PRODUCTS: 'Tüm ürünler'
}

const brandTabs = [
  {
    name: brandTabNameEnums.BRAND_ROW,
    label: brandTabLabelEnums.BRAND_ROW
  },
  {
    name: brandTabNameEnums.MORDOR_PRODUCTS,
    label: brandTabLabelEnums.MORDOR_PRODUCTS
  }
]

const UTAG_DATA_EVENT_NAME = 'storefront.voltran.analytics.utagData'
const UTAG_DATA_OVERRIDE_EVENT_NAME = 'storefront.voltran.analytics.utagData.override'
const UTAG_DATA_RESPONSE = 'storefront.voltran.analytics.utagData.response'
const INITIAL_LOCATION_SEARCH_REGEX = /puan|filtreler|siralama|markalar|kategori|sayfa/g
const LOCATION_SEARCH_REGEX = /puan|filtreler|siralama|markalar|kategori|sayfa/g
const TAB_CHANGE = 'BrandContent.TabChange'

const headerOption = DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.BRAND_HEADER]
const layoutOption = DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.BRAND_LAYOUT]
const moriaListingOption = DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.MORIA_LISTING]
const addToCartOption = DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.ADD_TO_CART]
const favoriteOption = DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.FAVORITE]

const BrandPage = memo((props) => {
  const {
    dispatch,
    location,
    match,
    brandPageBreadCrumbConfigArray,
    serverSideRendering,
    routeChange,
    seoInfo,
    userInfoPromise,
    pageFeatureToggleMap,
    fragmentsMap,
    brandInfo,
    brand_id,
    onlyProducts,
    defaultTab,
    isBot,
    keywords
  } = props

  const [isComponentRender, setIsComponentRender] = useState(false)
  const queryParams = useQueryParams()
  const [showBannerState, setShowBannerState] = useState(() => {
    if (location?.search?.search(INITIAL_LOCATION_SEARCH_REGEX) > 0) {
      return false
    }

    return true
  })
  const headerComponents = fragmentsMap?.get(headerOption?.id?.toString())
  const layoutComponents = fragmentsMap?.get(layoutOption?.id?.toString())
  const moriaComponents = fragmentsMap?.get(moriaListingOption?.id?.toString())
  const addToCartFragment = fragmentsMap?.get(addToCartOption?.id?.toString())
  const favoriteHandlerFragment = fragmentsMap?.get(favoriteOption?.id?.toString())

  useEffect(() => {
    const _boostrap = async () => {
      LoadEventManager.handlePageRenderStart(dispatch, DAVINCI_PAGES.BRAND, true)
      LoadEventManager.handleDomLoaded(dispatch, DAVINCI_PAGES.BRAND, true)

      userInfoPromise.then((userInfo) => {
        LoadEventManager.handlePageRendered(dispatch)
        sendWebtrekkData(userInfo)
      })

      if (!pageFeatureToggleMap) {
        resolveWithSettleContext(
          dispatch(
            featureToggleActionCreators.getBrandFeatureToggles.trigger(
              BRAND_PAGE_FEATURE_TOGGLES,
            ),
          )
        ).then(() => {
          fetchComponentsIfNecessary().then(() => {
            LoadEventManager.handlePageRendered(dispatch)
            handlePageRendered()
          })
        })
      } else {
        fetchComponentsIfNecessary().then(() => {
          LoadEventManager.handlePageRendered(dispatch)
          handlePageRendered()
        })
      }
      // handleGetOverrideData()
      handleGetSeoInformation()
      handleUtagOverrideDataListener()

      return () => {
        window?.HbEventBus?.off(UTAG_DATA_EVENT_NAME, false)
        dispatch(seoActionCreators.getGenericSeoInfo.cancel())
        dispatch(voltranActionCreators.getFragments.cancel())
      }
    }

    _boostrap()
  }, [])

  useEffect(() => {
    if (seoInfo) {
      window?.HbEventBus?.emit(UTAG_DATA_RESPONSE, {response: seoInfo})
      setSeoInfo(seoInfo)
    }
  }, [seoInfo])

  const fetchComponentsIfNecessary = async () => {
    await fetchBrandPageComponents()
    handlePageRendered()
  }

  const handlePageRendered = () => {
    if (process.env.BROWSER && document?.readyState === 'complete') {
      if (!isComponentRender) {
        setIsComponentRender(true)
      }
    }
  }

  const setSeoInfo = (seoInfo) => {
    if (seoInfo && process.env.BROWSER) {
      document.title = seoInfo.title
    }
  }

  const fetchBrandPageComponents = async () => {
    let fragments = []

    if (!addToCartFragment) {
      fragments.push(addToCartOption)
    }
    if (!favoriteHandlerFragment) {
      fragments.push(favoriteOption)
    }

    if (!layoutComponents) {
      const urlpostfix = match?.params?.brandId
      const rawCookies = process.env.BROWSER ? document.cookie : ''
      const moriaCookies = getCookiesByPrefix({rawCookies, prefixes: MORIA_PASS_COOKIE_PREFIXES})

      fragments.push({
        ...layoutOption,
        paramater: {
          ...layoutOption.paramater,
          ...queryParams,
          brandId: brand_id,
          urlpostfix,
          ...moriaCookies,
          isBot
        }
      })
    }

    return Promise.resolve()
  }

  const fetchSeo = (body = {}) => {
    const url = location.pathname + location.search
    const queryParams = queryString.parse(window?.location?.search)

    if (Object.keys(queryParams)?.length <= 1 && queryParams[PAGINATION_PARAM_NAMES.PAGE]) {
      delete queryParams[PAGINATION_PARAM_NAMES.PAGE]
    }

    resolveWithSettleContext(
      dispatch(
        seoActionCreators.getGenericSeoInfo.trigger({
          ...body,
          pageType: PAGE_TYPES.BRAND_DESKTOP,
          pageName: brandInfo?.name,
          pageValue: brandInfo?.name,
          absoluteUrl: removeQueryStringFromUrl(location.pathname) || '',
          isMobile: false,
          filters: generateQueryString(queryParams),
          url
        }),
      ),
    ).then((response) => {
      if (response?.result?.data) {
        window?.HbEventBus?.emit(UTAG_DATA_RESPONSE, {response: response.result.data.data})
        setSeoInfo(response.result.data.data)
      } else {
        window?.HbEventBus?.emit(UTAG_DATA_RESPONSE, {error: {success: false, message: response?.result?.message}})
      }
    }).catch((error) => {
      window?.HbEventBus?.emit(UTAG_DATA_RESPONSE, {error: error})
    })
  }

  const sendWebtrekkData = (info) => {
    const {
      dispatch,
      match: {
        params: {
          brandId,
        },
      },
      appliedFacetArray,
      queryParams,
      totalBrandProductCount,
    } = props
    const webtrekkData = generateBrandPageDesktopWebtrekkData(queryParams, appliedFacetArray, totalBrandProductCount, totalBrandProductCount, 'Markalar', brandId)
    generateUserInfoForWebtrekk(webtrekkData, info?.isAuthenticated)
    dispatch(analyticsActionCreators.sendWebtrekkEvent.trigger(webtrekkData))
  }

  const handleUtagOverrideDataListener = () => {
    window?.HbEventBus?.on(UTAG_DATA_OVERRIDE_EVENT_NAME, (data) => {
      window.utagData = {
        ...window.utagData,
        ...seoInfo?.utagData,
        ...data,
      }

      const location = window?.location?.search
      if (location?.search(LOCATION_SEARCH_REGEX) > 0) {
        setShowBannerState(false)
      } else {
        setShowBannerState(true)
      }
    })
  }

  const handleGetSeoInformation = () => {
    window?.HbEventBus?.on(UTAG_DATA_EVENT_NAME, (seoState) => {
      const body = {
        currentPage: seoState?.currentPage,
        appliedFilterCount: seoState?.appliedFilters?.length,
        pageName: match?.params?.brandId,
        appliedFilters: seoState?.appliedFilters,
        categoryInfo: seoState?.categoryInfo,
        totalProductCount: seoState?.totalProductCount,
      }
      fetchSeo(body)
    })
  }

  function getDisplayBrandName() {
    return seoInfo.h1 ? seoInfo.h1 : brandInfo.name
  }

  const [isTab, setIsTab] = useState(defaultTab)
  const [storeTabLoaded, setStoreTabLoaded] = useState(defaultTab === brandTabNameEnums.BRAND_ROW)
  const [allProductsTabLoaded, setAllProductsTabLoaded] = useState(defaultTab === brandTabNameEnums.MORDOR_PRODUCTS)

  useEffect(() => {
    window?.HbEventBus?.on(TAB_CHANGE, (data) => {
      setIsTab(data.tabName)
    })
  }, [])

  useEffect(() => {
    if (isTab === brandTabNameEnums.BRAND_ROW && !storeTabLoaded) {
      setStoreTabLoaded(true)
    }
    if (isTab === brandTabNameEnums.MORDOR_PRODUCTS && !allProductsTabLoaded) {
      setAllProductsTabLoaded(true)
    }
  }, [isTab])

  const scriptRenderMethodClientSide = routeChange || !serverSideRendering

  const {
    itemListElement,
    totalProductCount
  } = useMemo(() => MoriaComponentsHelper.getSeoState(moriaComponents), [moriaComponents])

  const structuredData = useMemo(() => (
    <ProductListStructuredData
      canonicalUrl={seoInfo?.canonicalUrl}
      title={seoInfo?.h1}
      isIndexedPage={seoInfo?.index === 'index'}
      itemListElement={itemListElement}
      totalProductCount={totalProductCount}
    />
  ), [])

  return (
    <div className={s.root}>
      <HbClientSideHelmet seoInfo={seoInfo} helmetClientChanged={setSeoInfo(seoInfo)}/>
      <div>
        <div className={s.breadcrumbs}>
          {
            brandPageBreadCrumbConfigArray?.length > 0 && (
              <HbBreadCrumbView
                forceRedirect
                location={location}
                rootIconEnabled={false}
                isSchemaStructuredActive
                itemConfigs={brandPageBreadCrumbConfigArray}/>
            )
          }
        </div>
        <div>
          {
            onlyProducts && (
              <div className={s.Wrapper}>
                <h1 className={s.BrandName} id={'page_title'} data-test-id={'header-h1'}>
                  {getDisplayBrandName()}
                </h1>
                <MoriaComponents
                  {...props}
                  pageType={DAVINCI_PAGE_TYPES.BRAND}
                  pageValue={brand_id}
                  moriaComponents={moriaComponents}
                  scriptRenderMethodClientSide={scriptRenderMethodClientSide}/>
              </div>
            )
          }
          {
            !onlyProducts && (
              <div className={s.Wrapper}>
                <VoltranFragment
                  options={DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.BRAND_HEADER]}
                  fragmentInfo={headerComponents}
                  renderMethodClientSide={scriptRenderMethodClientSide}/>

                <div className={s.Content}>
                  <div
                    id={'bc_rows'}
                    className={cx({
                      [s.Visible]: isTab === brandTabNameEnums.BRAND_ROW,
                      [s.Hide]: isTab !== brandTabNameEnums.BRAND_ROW
                    })}>
                    <VoltranFragment
                      options={DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.BRAND_LAYOUT]}
                      fragmentInfo={layoutComponents}
                      renderMethodClientSide={scriptRenderMethodClientSide}/>
                  </div>
                  <div
                    id={'bc_products'}
                    className={cx({
                      [s.Visible]: isTab === brandTabNameEnums.MORDOR_PRODUCTS,
                      [s.Hide]: isTab !== brandTabNameEnums.MORDOR_PRODUCTS
                    })}>
                    <MoriaComponents
                      {...props}
                      pageType={DAVINCI_PAGE_TYPES.BRAND}
                      pageValue={brand_id}
                      moriaComponents={moriaComponents}
                      scriptRenderMethodClientSide={scriptRenderMethodClientSide}/>
                  </div>
                </div>
              </div>
            )
          }
          {
            keywords?.length > 0 &&
            <SearchKeywordRowListView
              title={CATEGORY_SEARCH_KEYWORD_TITLE.POPULAR_KEYWORDS}
              pageTypeForDavinci={DAVINCI_PAGE_TYPES.BRAND}
              searchKeywordArray={keywords}/>
          }
        </div>
      </div>

      {structuredData}
    </div>
  )
})

BrandPage.redirectAsynchronously = (routeContext, fetchOperationContext, queryParams, type = HTTP_STATUS_CODES.FOUND) => {
  return new Promise((resolve, reject) => {
    fetchOperationContext.redirection = {
      type: type,
      url: removeQueryStringFromUrl(routeContext.pathname) +
        generateQueryString({
          ...queryParams,
          [PAGINATION_PARAM_NAMES.PAGE]: null,
        }),
    }
    resolve(fetchOperationContext)
  })
}

BrandPage.fetchData = async (dispatch, context) => {
  let result
  let fragments = []

  const {
    pathname,
    pathParams,
    isBot,
    queryParams,
    cookies,
    rawCookies,
    url,
  } = context

  const {
    brandId,
  } = pathParams

  const fetchOperationContext = {
    serverSideRendering: true,
    redirection: null,
    results: [],
    seoInfo: null,
    isNotFound: false,
    brandInfo: null,
    totalProductCount: 0,
    seoState: null,
  }

  let moriaData = {
    seoInfo: null,
    redirection: null,
    seoState: null,
    pageName: '',
  }

  const isForbiddenUrl = checkForBrandPageForbiddenUrlsRedirection(pathname)
  if (isForbiddenUrl) {
    if (isBot) {
      fetchOperationContext.redirection = isForbiddenUrl
    } else {
      fetchOperationContext.isNotFound = true
    }
    return fetchOperationContext
  }

  const locationCookie = jsonParse(cookies?.location)

  const getFeatureTogglesResponse = await resolveWithSettleContext(dispatch(
    featureToggleActionCreators.getBrandFeatureToggles.trigger(
      BRAND_PAGE_FEATURE_TOGGLES,
    ),
  ))

  if (getFeatureTogglesResponse.hasError) {
    fetchOperationContext.isError = getFeatureTogglesResponse.result
    return fetchOperationContext
  }

  const featureToggleMap = generateFeatureToggleMap(getFeatureTogglesResponse.result?.data?.data)

  if (getFeatureTogglesResponse?.result?.data?.data) {
    fetchOperationContext.featureToggles = getFeatureTogglesResponse.result?.data?.data
  }

  if (!isBot && !featureToggleMap?.get(FEATURE_TOGGLE_NAMES.SERVER_SIDE_RENDER_FOR_PWA_BRAND_PAGE)) {
    fetchOperationContext.serverSideRendering = false
  }

  if (isBrandPageApiPaginationLimitExceeded(queryParams[PAGINATION_PARAM_NAMES.PAGE], featureToggleMap?.get(FEATURE_TOGGLE_NAMES.PAGE_LIMIT_IN_LISTING_PAGES))) {
    result = await BrandPage.redirectAsynchronously(
      context,
      fetchOperationContext,
      queryParams,
    )
  } else if (isBot || featureToggleMap?.get(FEATURE_TOGGLE_NAMES.SERVER_TO_CLIENT_SEND_INITIAL_DATA_FOR_PWA_BRAND_PAGE)) {
    const getBrandInfoResponse = await resolveWithSettleContext(
      dispatch(
        brandActionCreators.getBrandInfo.trigger(
          {
            ...queryParams,
            brandId,
          }
        )
      )
    )

    if (getBrandInfoResponse.hasError) {
      fetchOperationContext.isError = getBrandInfoResponse.result
      return fetchOperationContext
    }

    const brandInfo = getBrandInfoResponse?.result?.data?.data
    const brandName = brandInfo?.brand?.name
    const brandUrl = brandInfo?.brand?.url
    const brandKey = brandInfo?.brand?.key

    if (!brandInfo.hasBrand) {
      fetchOperationContext.isNotFound = true
      return fetchOperationContext
    }

    if (brandUrl && brandId !== brandUrl) {
      result = await BrandPage.redirectAsynchronously(
        {
          pathname: brandUrl,
        },
        fetchOperationContext,
        queryParams,
        HTTP_STATUS_CODES.MOVED_PERMANENTLY,
      )

      return result
    }

    if (brandInfo?.brand?.itemCount === 0) {
      fetchOperationContext.redirection = {
        isInternal: true,
        type: HTTP_STATUS_CODES.MOVED_PERMANENTLY,
        url: '/markalar',
      }
      return fetchOperationContext
    }

    const getBrandByProductBrandNameResponse = await resolveWithSettleContext(
      dispatch(
        brandActionCreators.getBrandByProductBrandName.trigger(
          {
            [FACET_QUERY_PARAM_NAMES.BRAND_NAME]: encodeURIComponent(brandName),
          },
          cookies
        )
      )
    )

    if (getBrandByProductBrandNameResponse?.result?.data) {
      fetchOperationContext.brandInfo = getBrandByProductBrandNameResponse.result.data
    }
    let showOnlyProducts = fetchOperationContext.brandInfo === null || fetchOperationContext.brandInfo.status !== 'Active' || fetchOperationContext.brandInfo.isSuspended

    const moriaCookies = getCookiesByPrefix({rawCookies, prefixes: MORIA_PASS_COOKIE_PREFIXES})

    fragments.push({
      ...moriaListingOption,
      customApiUrl: {
        serverUrl: `${appConfig.moriaApi.serverUrl}/components/VerticalFilter,SortingBox,JetDelivery,HeroFilter,ProductsBanner,ProductList/${encodeURIComponent(brandKey)}`,
        clientUrl: `${appConfig.moriaApi.clientUrl}/components/VerticalFilter,SortingBox,JetDelivery,HeroFilter,ProductsBanner,ProductList/${encodeURIComponent(brandKey)}`,
      },
      paramater: {
        ...moriaListingOption.paramater,
        ...queryParams,
        stickyPartials: 'VerticalFilter',
        [FACET_QUERY_PARAM_NAMES.CITY_CODE]: locationCookie?.city?.code,
        [FACET_QUERY_PARAM_NAMES.RECEIVER_CITY]: locationCookie?.city?.name,
        [FACET_QUERY_PARAM_NAMES.RECEIVER_TOWN]: locationCookie?.town?.name,
        [FACET_QUERY_PARAM_NAMES.RECEIVER_DISTRICT]: locationCookie?.district?.name,
        isBot
      },
      headers: moriaCookies
    })

    if (!showOnlyProducts) {
      fragments.push({
        ...layoutOption,
        paramater: {
          ...layoutOption.paramater,
          ...queryParams,
          brandId: fetchOperationContext.brandInfo?.id,
          urlpostfix: brandId,
          ...moriaCookies,
          isBot
        },
      })
    }

    const fragmentsResponse = await resolveWithSettleContext(
      dispatch(
        voltranActionCreators.getFragments.trigger(fragments),
      ),
    )

    if (fragmentsResponse?.result?.[0]?.result?.status !== 200) {
      fetchOperationContext.isError = true
      return fetchOperationContext
    }

    fetchOperationContext.redirection = checkForDesktopBrandPageRedirection(fragmentsResponse?.result?.[0]?.result?.data?.ProductList.redirection, fragmentsResponse?.result?.[0]?.result?.data?.ProductList.seoState, brandId, queryParams)


    const fragmentSeoState = fragmentsResponse
      ?.result?.[0]?.result?.data
      ?.ProductList.seoState

    const categoryHierarchy = fragmentSeoState?.categoryInfo?.categoryHierarchy
    const parentCategoryName = fragmentSeoState?.parentCategoryName

    await resolveWithSettleContext(
      dispatch(
        footerActionCreators.getFooterItems.trigger({
          categoryHierarchy: categoryHierarchy,
          platform: 'desktop',
          categoryName: parentCategoryName
        })
      )
    )

    const getGenericSeoInfoResponse = await resolveWithSettleContext(
      dispatch(
        seoActionCreators.getGenericSeoInfo.trigger({
          pageType: 'brand',
          pageValue: brandName,
          pageName: brandUrl,
          isMobile: false,
          filters: generateQueryString(context.query),
          absoluteUrl: removeQueryStringFromUrl(pathname) || '',
          totalProductCount: fragmentsResponse?.result?.[0]?.result?.data?.ProductList.seoState?.totalProductCount,
          appliedFilters: fragmentsResponse?.result?.[0]?.result?.data?.ProductList.seoState?.appliedFilters,
          categoryInfo: fragmentsResponse?.result?.[0]?.result?.data?.ProductList.seoState?.categoryInfo,
          currentPage: fragmentsResponse?.result?.[0]?.result?.data?.ProductList.seoState?.currentPage,
          fullPath: url,
        })
      )
    )

    fetchOperationContext.seoInfo = getGenericSeoInfoResponse?.result?.data?.data
    dispatch({type: 'SET_BRAND_SEO_INFO', payload: {...getGenericSeoInfoResponse?.result?.data?.data}})

    if (showOnlyProducts) {
      await resolvePromiseList(
        Promise.all([
          featureToggleMap.get(FEATURE_TOGGLE_NAMES.DISPLAY_POPULAR_SEARCH_KEYWORDS_ON_CATEGORY_PAGE) &&
          resolveWithSettleContext(
            dispatch(
              categoryActionCreators.getKeywords.trigger({
                brand: encodeURIComponent(brandName),
              })
            )
          )
        ])).then((response) => {
        return fetchOperationContext
      })
    } else {
      fragments.push({
        ...headerOption,
        paramater: {
          ...headerOption.paramater,
          ...queryParams,
          urlpostfix: brandId,
          brandId: fetchOperationContext.brandInfo?.id,
          h1title: getGenericSeoInfoResponse?.result?.data?.data?.h1,
          totalProductCount: fragmentsResponse?.result?.[0]?.result?.data?.ProductList.seoState?.totalProductCount,
          showLayoutTab: fragmentsResponse?.result?.[1]?.result?.data?.seoState?.showLayoutTab,
          isBot
        },
      })

      await resolvePromiseList(
        Promise.all([
          resolveWithSettleContext(
            dispatch(
              voltranActionCreators.getFragments.trigger([fragments[2]]),
            ),
          ),
          featureToggleMap.get(FEATURE_TOGGLE_NAMES.DISPLAY_POPULAR_SEARCH_KEYWORDS_ON_CATEGORY_PAGE) &&
          resolveWithSettleContext(
            dispatch(
              categoryActionCreators.getKeywords.trigger({
                brand: encodeURIComponent(brandName),
              })
            )
          )
        ])).then(([fragmentsResponse]) => {
        const defaultTab = fragmentsResponse?.result?.[0]?.result?.data?.seoState?.defaultTab

        dispatch({type: 'SET_BRAND_DEFAULT_TAB', payload: defaultTab})

        return fetchOperationContext
      })
    }
  } else {
    result = fetchOperationContext
  }

  result = fetchOperationContext
  return result
}

BrandPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  pageFeatureToggleMap: ImmutablePropTypes.map,
  actions: PropTypes.object,
  location: PropTypes.object,
  userInfo: PropTypes.object,
  routeChange: PropTypes.bool,
  platform: PropTypes.string,
  isMobile: PropTypes.bool,
}

const mapStateToProps = (state) => {
  const {
    accountState: {
      userInfo,
    },
    featureToggleState: {
      pageFeatureToggleMap,
    },
    brandState: {
      brandProductArray,
      brandProductPaginationParams,
      brandSeoInfo,
      totalBrandProductCount,
      brandSearchPass,
      initialPageNumber,
      brandShippingType,
      brandInfo,
      brand_id,
      onlyProducts,
      defaultTab,
    },
    seoState: {
      seoInfo,
    },
    cartState: {
      cartCandidateProduct,
    },
    voltranState: {
      fragmentsMap,
    },
    userAgentState: {
      isBot,
    },
    categoryState: {
      keywords,
    },
  } = state

  return {
    pageFeatureToggleMap,
    userInfo,
    brandProductArray,
    brandProductPaginationParams,
    brandInfo,
    brandSeoInfo,
    totalBrandProductCount,
    brandSearchPass,
    brandShippingType,
    initialPageNumber,
    seoInfo,
    cartCandidateProduct,
    brandPageBreadCrumbConfigArray: seoBreadCrumbConfigArraySelector(state),
    fragmentsMap,
    brand_id,
    onlyProducts,
    defaultTab,
    isBot,
    keywords
  }
}

export default connect(mapStateToProps)(
  hbPageWithUserInfo(
    hbPageWithAnalytics(
      BrandPage,
    )
  )
)
